import { useEffect } from 'react';
import { useLocation } from 'react-router';

const DATA_MARK = 'lexgat-statute-guard';

/**
 * Hardens the document for statute surfaces.
 *
 * On public `/statutes/*` routes, allows indexing (public corpus).
 * On authenticated `/library/*` routes, blocks indexing (private app).
 */
export function useStatuteAntiScrapeDocument() {
  const { pathname } = useLocation();
  const isPublicStatuteRoute = pathname.startsWith('/statutes');

  useEffect(() => {
    const specs: { name?: string; httpEquiv?: string; content: string }[] = isPublicStatuteRoute
      ? [
          { name: 'robots', content: 'index, follow' },
          {
            name: 'googlebot',
            content: 'index, follow, max-snippet:-1, max-image-preview:large',
          },
          { name: 'bingbot', content: 'index, follow' },
          { name: 'referrer', content: 'strict-origin-when-cross-origin' },
        ]
      : [
          { name: 'robots', content: 'noindex, nofollow, noarchive, nosnippet, noimageindex' },
          {
            name: 'googlebot',
            content: 'noindex, nofollow, noarchive, nosnippet, max-image-preview:none',
          },
          { name: 'bingbot', content: 'noindex, nofollow' },
          { name: 'referrer', content: 'strict-origin-when-cross-origin' },
        ];

    const created: HTMLElement[] = [];
    for (const spec of specs) {
      const el = document.createElement('meta');
      el.setAttribute(`data-${DATA_MARK}`, '1');
      if (spec.name) el.setAttribute('name', spec.name);
      if (spec.httpEquiv) el.setAttribute('http-equiv', spec.httpEquiv);
      el.setAttribute('content', spec.content);
      document.head.appendChild(el);
      created.push(el);
    }

    return () => {
      for (const el of created) el.remove();
    };
  }, [isPublicStatuteRoute]);
}
